import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const exportColumns = [
  ["student_rollno", "Student No."],
  ["student_name", "Student Name"],
  ["year_number", "Year"],
  ["semester_number", "Semester"],
  ["fee_amount", "Amount"],
  ["remarks", "Remarks"],
  ["workflow_status", "Status"],
];

export default function FeeBookApproval() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const modifiedBy = user?.user_email || user?.username || "";
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selection, setSelection] = useState([]);
  const [approving, setApproving] = useState(false);
  const [exportMenu, setExportMenu] = useState({ anchorEl: null, rows: [], label: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const showMessage = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchGroups = useCallback(async () => {
    if (!clientId || !collegeId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/approval-groups/list`
      );
      setGroups(
        (data?.data || []).map((group) => ({
          ...group,
          id: `${group.fee_head_id}-${group.workflow_status}`,
        }))
      );
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to load Fee Book approvals", "error");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, clientId, collegeId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const openStudents = (group) => {
    setSelectedGroup(group);
    setSelection(group.students.map((student) => String(student.fee_book_id)));
  };

  const declineSelected = async () => {
    if (!selection.length) return showMessage("Select at least one student", "warning");
    setApproving(true);
    try {
      const { data } = await axios.patch(
        `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/approval-groups/decline`,
        { fee_book_ids: selection, modified_by: modifiedBy }
      );
      showMessage(data?.message || "Selected Fee Books declined successfully");
      setSelectedGroup(null);
      setSelection([]);
      await fetchGroups();
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to decline selected students", "error");
    } finally {
      setApproving(false);
    }
  };

  const approveSelected = async () => {
    if (!selection.length) return showMessage("Select at least one student", "warning");
    setApproving(true);
    try {
      const { data } = await axios.patch(
        `${API_BASE_URL}/api/fee-books/${clientId}/${collegeId}/approval-groups/approve`,
        { fee_book_ids: selection, modified_by: modifiedBy }
      );
      showMessage(data?.message || "Selected Fee Books approved successfully");
      setSelectedGroup(null);
      setSelection([]);
      await fetchGroups();
    } catch (error) {
      showMessage(error?.response?.data?.message || "Unable to approve selected students", "error");
    } finally {
      setApproving(false);
    }
  };

  const exportExcel = (rows, label) => {
    const data = rows.map((row) =>
      Object.fromEntries(
        exportColumns.map(([field, title]) => [
          title,
          field === "fee_amount" ? Number(row[field] || 0).toFixed(2) : row[field] ?? "",
        ])
      )
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), label);
    XLSX.writeFile(workbook, `Fee_Book_${label}.xlsx`);
  };

  const exportPdf = (rows, label) => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.text(`Fee Book ${label}`, 14, 12);
    autoTable(doc, {
      startY: 18,
      head: [exportColumns.map(([, title]) => title)],
      body: rows.map((row) =>
        exportColumns.map(([field]) =>
          field === "fee_amount" ? Number(row[field] || 0).toFixed(2) : row[field] ?? ""
        )
      ),
      headStyles: { fillColor: [25, 118, 210] },
    });
    doc.save(`Fee_Book_${label}.pdf`);
  };

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 80,
        sortable: false,
        valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },
      { field: "fee_book_name", headerName: "Fee Book Name", minWidth: 260, flex: 1 },
      { field: "no_of_students", headerName: "No. of Students", width: 160 },
      {
        field: "amount",
        headerName: "Amount",
        width: 150,
        valueFormatter: ({ value }) => Number(value || 0).toFixed(2),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <Button
            size="medium"
            variant="contained"
            aria-label={`View students for ${row.fee_book_name}`}
            startIcon={<VisibilityIcon />}
            onClick={() => openStudents(row)}
            sx={{ color: "#fff" }}
          >
            View
          </Button>
        ),
      },
    ],
    []
  );

  const studentColumns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 80,
    },
    { field: "student_rollno", headerName: "Student No.", minWidth: 150, flex: 0.7 },
    { field: "student_name", headerName: "Student Name", minWidth: 220, flex: 1 },
    { field: "year_number", headerName: "Year", width: 80 },
    { field: "semester_number", headerName: "Semester", width: 100 },
    {
      field: "fee_amount",
      headerName: "Amount",
      width: 120,
      valueFormatter: ({ value }) => Number(value || 0).toFixed(2),
    },
    { field: "remarks", headerName: "Remarks", minWidth: 180, flex: 0.8 },
  ];

  const renderSection = (title, rows) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} mb={1}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          {title} ({rows.length})
        </Typography>
        <Typography variant="subtitle1" fontWeight="bold">
          Total Amount: {rows.reduce((sum, row) => sum + Number(row.amount || 0), 0).toFixed(2)}
        </Typography>
      </Stack>
      <Box sx={{ height: 410, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize={pageSize}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          pagination
          disableSelectionOnClick
        />
      </Box>
    </Paper>
  );

  const draftGroups = groups.filter((group) => group.workflow_status === "DRAFT");
  const approvedGroups = groups.filter((group) => group.workflow_status === "APPROVED");

  return (
    <Box p={2}>
      <Stack spacing={2}>
        {renderSection("Pending Fee Book Approvals", draftGroups)}
        {renderSection("Approved Fee Books", approvedGroups)}
      </Stack>

      <Dialog
        open={Boolean(selectedGroup)}
        onClose={() => setSelectedGroup(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">{selectedGroup?.fee_book_name} - Students</Typography>
              <Typography variant="body2">
                Total Students: {selectedGroup?.no_of_students || 0} | Total Amount:{" "}
                {Number(selectedGroup?.amount || 0).toFixed(2)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<MoreVertIcon />}
              sx={{ color: "#fff" }}
              onClick={(event) =>
                setExportMenu({
                  anchorEl: event.currentTarget,
                  rows: selectedGroup?.students || [],
                  label: selectedGroup?.workflow_status || "Fee_Book",
                })
              }
            >
              Actions
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 470, width: "100%", mt: 1 }}>
            <DataGrid
              rows={(selectedGroup?.students || []).map((row, index) => ({
                ...row,
                sno: index + 1,
                id: String(row.fee_book_id),
              }))}
              columns={studentColumns}
              checkboxSelection
              selectionModel={selection}
              onSelectionModelChange={(ids) => setSelection(ids.map(String))}
              pageSize={pageSize}
              page={page}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              pagination
              disableSelectionOnClick
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedGroup(null)}>Close</Button>
          {selectedGroup?.workflow_status === "DRAFT" && (
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              disabled={!selection.length || approving}
              onClick={approveSelected}
              sx={{
                backgroundColor: "#1976d2",
                color: "#fff",
                "&:hover": { backgroundColor: "#115293" },
              }}
            >
              Approve Selected ({selection.length})
            </Button>
          )}
          {selectedGroup?.workflow_status === "APPROVED" && (
            <Button
              variant="contained"
              startIcon={<CancelIcon />}
              disabled={!selection.length || approving}
              onClick={declineSelected}
              sx={{
                backgroundColor: "#d32f2f",
                color: "#fff",
                "&:hover": { backgroundColor: "#b71c1c" },
              }}
            >
              Decline Selected ({selection.length})
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={exportMenu.anchorEl}
        open={Boolean(exportMenu.anchorEl)}
        onClose={() => setExportMenu({ anchorEl: null, rows: [], label: "" })}
      >
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            sx={{ backgroundColor: "#d32f2f", color: "#fff" }}
            onClick={() => {
              exportPdf(exportMenu.rows, exportMenu.label);
              setExportMenu({ anchorEl: null, rows: [], label: "" });
            }}
          >
            DOWNLOAD PDF
          </Button>
        </MenuItem>
        <MenuItem disableRipple>
          <Button
            fullWidth
            variant="contained"
            startIcon={<TableViewIcon />}
            sx={{ backgroundColor: "#4caf50", color: "#fff" }}
            onClick={() => {
              exportExcel(exportMenu.rows, exportMenu.label);
              setExportMenu({ anchorEl: null, rows: [], label: "" });
            }}
          >
            DOWNLOAD EXCEL
          </Button>
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((old) => ({ ...old, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
